import { useEffect } from 'react';
import { AVAILABILITY_WS_EVENTS } from '../../../../socket/constants';
import { subscribeAvailabilitySocketEvent } from '../../../../socket/events';
import { AvailabilitySocketActivityPayload, AvailabilitySocketInitialStatePayload } from '../../../../socket/types';
import { StatusType } from '../../../constants';
import { getStatusFromActivity, matchesCurrentUser, toStatusType } from '../../../helpers';
import { CurrentAvailabilityUser } from '../../../types';

type UseAvailabilityCurrentUserListenersParams = Readonly<{
  currentUser: CurrentAvailabilityUser | null;
  enabled: boolean;
  initCurrentUser: (user: CurrentAvailabilityUser) => void;
  setCurrentUserStatus: (status: StatusType) => void;
}>;

const useAvailabilityCurrentUserListeners = ({
  currentUser,
  enabled,
  initCurrentUser,
  setCurrentUserStatus,
}: UseAvailabilityCurrentUserListenersParams) => {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const unsubscribeInitialState = subscribeAvailabilitySocketEvent<AvailabilitySocketInitialStatePayload>(
      AVAILABILITY_WS_EVENTS.INITIAL_STATE,
      payload => {
        const payloadUser = payload?.user;
        const nextId = payloadUser?.employeeAlias || payloadUser?.id || currentUser?.id;

        if (!nextId) {
          return;
        }

        initCurrentUser({
          id: nextId,
          email: payloadUser?.email || currentUser?.email || '',
          name: payloadUser?.name || currentUser?.name || nextId,
          status: toStatusType(payload?.status?.state ?? payload?.status?.status ?? payloadUser?.status),
        });
      },
    );

    const unsubscribeActivityUpdate = subscribeAvailabilitySocketEvent<AvailabilitySocketActivityPayload>(
      AVAILABILITY_WS_EVENTS.ACTIVITY_UPDATE,
      payload => {
        if (!currentUser || !matchesCurrentUser(currentUser, payload)) {
          return;
        }

        const nextStatus = getStatusFromActivity(payload);

        if (currentUser.status !== nextStatus) {
          setCurrentUserStatus(nextStatus);
        }
      },
    );

    return () => {
      unsubscribeInitialState();
      unsubscribeActivityUpdate();
    };
  }, [currentUser, enabled, initCurrentUser, setCurrentUserStatus]);
};

export default useAvailabilityCurrentUserListeners;
